import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { styled } from "@pigment-css/react";

// Without offsets the probe takes the caption's static position, so it leaves the scrollport exactly
// as the caption pins. In a flex parent that static position is the content-box start, which is the
// caption's own top only while the caption is the first child.
const PinProbe = styled("span")({
  position: "absolute",
  width: 1,
  height: 1,
  pointerEvents: "none",
});

const Sticky = styled("div")(({ theme }) => ({
  position: "sticky",
  top: 0,
  zIndex: 1,
  "&::before, &::after": {
    content: '""',
    position: "absolute",
    zIndex: -1,
    insetInline: 0,
    pointerEvents: "none",
    opacity: 0,
    transition: `opacity ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.scrollPin}`,
  },
  "&::before": {
    top: 0,
    bottom: 0,
    background: theme.fieldSalesTwo.colors.surface.glassFlat,
  },
  "&::after": {
    bottom: `-${theme.fieldSalesTwo.spacing[24]}`,
    height: theme.fieldSalesTwo.spacing[24],
    background: `linear-gradient(to bottom, ${theme.fieldSalesTwo.colors.surface.glassFlat}, transparent)`,
  },
  '&[data-pinned="true"]': {
    "&::before, &::after": { opacity: 1 },
  },
  "@media (prefers-reduced-motion: reduce)": {
    "&::before, &::after": { transition: "none" },
  },
}));

export interface PinnedCaptionProps {
  scrollRef: RefObject<HTMLElement | null>;
  className?: string;
  children?: ReactNode;
}

/** Sets `data-pinned="true"` while content is scrolled under the caption, and draws the pinned surface in its `::before` and `::after`. */
export function PinnedCaption({ scrollRef, className, children }: PinnedCaptionProps) {
  const probeRef = useRef<HTMLSpanElement>(null);
  const [pinned, setPinned] = useState(false);

  useEffect(() => {
    const root = scrollRef.current;
    const probe = probeRef.current;
    if (!root || !probe) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        setPinned(!entry.isIntersecting && entry.boundingClientRect.top < (entry.rootBounds?.top ?? 0));
      },
      { root },
    );
    observer.observe(probe);
    return () => observer.disconnect();
  }, [scrollRef]);

  return (
    <>
      <PinProbe ref={probeRef} aria-hidden="true" />
      <Sticky className={className} data-pinned={pinned}>
        {children}
      </Sticky>
    </>
  );
}
